import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import { PageHeader, categoryColor, priorityColor } from '@/components/PageHeader';
import { shareWhatsApp, shareBrandedPDF } from '@/lib/share';
import { usePlayer } from '@/context/PlayerContext';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, ChevronLeft, ChevronRight, Video, Clock, Trash2, Share2, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Lun', 'Mar', 'Mi\u00e9', 'Jue', 'Vie', 'S\u00e1b', 'Dom'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [dayModal, setDayModal] = useState(null); // Date | null
  const [form, setForm] = useState({ title: '', category: 'meeting', start: '', end: '', project_id: '', meeting_url: '', description: '', recurrence: 'none', recurrence_until: '' });
  const { play } = usePlayer();

  // Compartir cita con marca: título, inicio, fin (si existe) y temas a abordar.
  const fmtDT = (iso) => new Date(iso).toLocaleString('es', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const fmtT = (iso) => new Date(iso).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  const eventPdfLines = (e) => {
    const lines = [{ k: 'Inicio', v: fmtDT(e.start) }];
    if (e.end) lines.push({ k: 'Fin', v: fmtT(e.end) });
    lines.push({ k: 'Categoría', v: categoryColor(e.category).label });
    if (e.meeting_url) lines.push({ k: 'Enlace de video', v: e.meeting_url });
    lines.push({ k: 'Temas a abordar', v: e.description || 'Sin temas registrados' });
    return lines;
  };
  const shareEventPDF = (e) => shareBrandedPDF(e.title, eventPdfLines(e), 'Detalle de la cita');
  const shareEventWA = (e) => {
    const lines = [`Inicio: ${fmtDT(e.start)}`];
    if (e.end) lines.push(`Fin: ${fmtT(e.end)}`);
    lines.push(`Categoría: ${categoryColor(e.category).label}`);
    if (e.meeting_url) lines.push(`Video: ${e.meeting_url}`);
    lines.push('', 'Temas a abordar:', e.description || 'Sin temas registrados');
    shareWhatsApp(e.title, lines);
  };

  const load = async () => {
    const [e, p, t, sp] = await Promise.all([api.get('/events'), api.get('/projects').catch(() => ({ data: [] })), api.get('/tasks').catch(() => ({ data: [] })), api.get('/spaces').catch(() => ({ data: [] }))]);
    setEvents(e.data); setProjects(p.data); setTasks(t.data); setSpaces(sp.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.start) { toast.error('T\u00edtulo y fecha requeridos'); return; }
    try {
      const provider = form.meeting_url?.includes('zoom') ? 'zoom' : form.meeting_url ? 'meet' : null;
      await api.post('/events', { ...form, meeting_provider: provider, project_id: form.project_id || null });
      toast.success('Evento creado'); setOpen(false);
      setForm({ title: '', category: 'meeting', start: '', end: '', project_id: '', meeting_url: '', description: '', recurrence: 'none', recurrence_until: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const del = async (id) => { await api.delete(`/events/${id}`).catch((e) => toast.error(e?.response?.data?.detail || 'Sin permiso')); load(); };

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1); let startDay = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = []; for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  const evByDay = (date) => events.filter((e) => new Date(e.start).toDateString() === date.toDateString());
  const tasksByDay = (date) => tasks.filter((t) => t.due_date && new Date(t.due_date).toDateString() === date.toDateString());
  const spacesByDay = (date) => spaces.filter((s) => s.date === `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);

  const listEvents = (() => {
    const now = new Date();
    if (view === 'day') return events.filter((e) => new Date(e.start).toDateString() === now.toDateString());
    if (view === 'week') { const wk = new Date(now); const day = (wk.getDay() + 6) % 7; const mon = new Date(wk); mon.setDate(wk.getDate() - day); const sun = new Date(mon); sun.setDate(mon.getDate() + 6); return events.filter((e) => { const d = new Date(e.start); return d >= mon && d <= sun; }); }
    return [...events].filter((e) => new Date(e.start) >= new Date(Date.now() - 864e5)).sort((a, b) => new Date(a.start) - new Date(b.start));
  })().sort((a, b) => new Date(a.start) - new Date(b.start));

  return (
    <div data-testid="calendar-module">
      <PageHeader kicker="Calendario" title="Calendario Unificado" subtitle="Google, Outlook e iCal en una vista cinematogr\u00e1fica."
        actions={<button data-testid="new-event-btn" onClick={() => setOpen(true)} className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}><Plus size={16} /> Evento</button>} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="calendar-view-tabs" className="pp-glass">
            <TabsTrigger value="month">Mes</TabsTrigger><TabsTrigger value="week">Semana</TabsTrigger>
            <TabsTrigger value="day">D\u00eda</TabsTrigger><TabsTrigger value="agenda">Agenda</TabsTrigger>
          </TabsList>
        </Tabs>
        {view === 'month' && (
          <div className="flex items-center gap-3">
            <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-[rgba(255,255,255,0.06)]"><ChevronLeft size={18} /></button>
            <span className="font-semibold min-w-[140px] text-center">{MONTHS[m]} {y}</span>
            <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-[rgba(255,255,255,0.06)]"><ChevronRight size={18} /></button>
          </div>
        )}
      </div>

      {view === 'month' ? (
        <div className="pp-glass p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">{DAYS.map((d) => <div key={d} className="text-center text-xs text-muted-foreground font-medium py-1">{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, i) => (
              <div key={i} onClick={() => date && setDayModal(date)} data-testid={date ? `calendar-day-${date.getDate()}` : undefined}
                className={`min-h-[92px] rounded-xl p-1.5 border ${date && date.toDateString() === new Date().toDateString() ? 'border-[#3EB489] pp-glow-mint' : 'border-[rgba(255,255,255,0.05)]'} ${!date ? 'opacity-0 pointer-events-none' : 'hover:bg-[rgba(62,180,137,0.08)] cursor-pointer'}`}>
                {date && <>
                  <div className="text-xs text-muted-foreground mb-1 flex items-center justify-between">
                    <span>{date.getDate()}</span>
                    <span className="flex items-center gap-1">
                      {spacesByDay(date).length > 0 && <span title="PlannerSpace ocupado" className="text-[9px] px-1 rounded font-bold" style={{ background: 'rgba(201,168,76,0.22)', color: '#C9A84C' }}>●{spacesByDay(date).length}</span>}
                      {tasksByDay(date).length > 0 && <span className="text-[9px] px-1 rounded" style={{ background: 'rgba(62,180,137,0.2)', color: '#3EB489' }}>{tasksByDay(date).length}✓</span>}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {evByDay(date).slice(0, 3).map((e) => { const c = categoryColor(e.category); return (
                      <div key={e.id} className="text-[10px] px-1.5 py-0.5 rounded-md truncate" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>{e.title}</div>
                    ); })}
                    {evByDay(date).length > 3 && <div className="text-[9px] text-muted-foreground pl-1">+{evByDay(date).length - 3} más</div>}
                  </div>
                </>}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {listEvents.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground">Sin eventos.</div>}
          {listEvents.map((e) => { const c = categoryColor(e.category); return (
            <motion.div key={e.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="pp-glass p-4 flex items-center gap-4 group">
              <div className="text-center min-w-[52px]"><div className="text-lg font-bold">{new Date(e.start).getDate()}</div><div className="text-[10px] text-muted-foreground uppercase">{MONTHS[new Date(e.start).getMonth()].slice(0, 3)}</div></div>
              <div className="w-1 self-stretch rounded-full" style={{ background: c.text }} />
              <div className="flex-1">
                <div className="font-medium">{e.title}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2"><Clock size={11} /> {new Date(e.start).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                  <span className="px-1.5 py-0.5 rounded" style={{ background: c.bg, color: c.text }}>{c.label}</span>
                  {e.prepared_by && <span className="pp-gold">\u2022 preparado por {e.prepared_by}</span>}
                </div>
              </div>
              {e.meeting_url && <button onClick={() => play({ id: 'dQw4w9WgXcQ', title: e.title })} className="h-8 px-3 rounded-lg text-xs flex items-center gap-1 pp-mint border border-[rgba(62,180,137,0.3)]"><Video size={13} /> Unirse</button>}
              <button onClick={() => del(e.id)} className="opacity-0 group-hover:opacity-70 hover:opacity-100 text-red-400"><Trash2 size={15} /></button>
            </motion.div>
          ); })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="pp-glass-strong">
          <DialogHeader><DialogTitle>Nuevo evento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="T\u00edtulo" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="event-start" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
              <input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['meeting', 'task', 'personal', 'urgent'].map((cat) => { const c = categoryColor(cat); return (
                <button key={cat} onClick={() => setForm({ ...form, category: cat })} className={`px-3 h-9 rounded-lg text-xs border ${form.category === cat ? '' : 'opacity-50'}`} style={{ background: c.bg, color: c.text, borderColor: c.border }}>{c.label}</button>
              ); })}
            </div>
            <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none">
              <option value="">Sin proyecto</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} placeholder="Link Zoom/Meet (opcional)" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.03)] space-y-2">
              <div className="text-xs text-muted-foreground">Recurrencia</div>
              <div className="flex gap-2 flex-wrap">
                {[['none', 'Única'], ['daily', 'Diaria'], ['weekly', 'Semanal'], ['monthly', 'Mensual']].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`event-recur-${v}`} onClick={() => setForm({ ...form, recurrence: v })} className={`px-3 h-9 rounded-lg text-xs border ${form.recurrence === v ? 'pp-mint border-[#3EB489] bg-[rgba(62,180,137,0.12)]' : 'text-muted-foreground'}`}>{l}</button>
                ))}
              </div>
              {form.recurrence !== 'none' && (
                <div><div className="text-[11px] text-muted-foreground mb-1">Repetir hasta</div><input type="date" data-testid="event-recur-until" value={form.recurrence_until} onChange={(e) => setForm({ ...form, recurrence_until: e.target.value })} className="w-full h-10 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none" /></div>
              )}
            </div>
            <button data-testid="event-save" onClick={save} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Día expandido: detalle con eventos + tareas del día */}
      <Dialog open={!!dayModal} onOpenChange={(o) => !o && setDayModal(null)}>
        <DialogContent className="pp-glass-strong max-h-[85vh] overflow-y-auto pp-scroll" data-testid="day-detail-modal">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {dayModal && `${DAYS[(dayModal.getDay() + 6) % 7]}, ${dayModal.getDate()} de ${MONTHS[dayModal.getMonth()]} ${dayModal.getFullYear()}`}
            </DialogTitle>
          </DialogHeader>
          {dayModal && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <button data-testid="day-add-event" onClick={() => {
                const d = new Date(dayModal); d.setHours(9, 0, 0, 0);
                const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                setForm({ title: '', category: 'meeting', start: iso, end: '', project_id: '', meeting_url: '', description: '', recurrence: 'none', recurrence_until: '' });
                setDayModal(null); setOpen(true);
              }} className="flex-1 h-10 rounded-xl text-sm font-medium text-[#07090A] flex items-center justify-center gap-2" style={{ background: '#3EB489' }}><Plus size={16} /> Agregar evento</button>
                <button data-testid="day-share-pdf" onClick={() => shareBrandedPDF(dayModal.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' }), [...evByDay(dayModal).map((e) => ({ k: new Date(e.start).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }), v: e.title })), ...tasksByDay(dayModal).map((t) => ({ k: 'Tarea', v: t.title }))], 'Agenda del día')} className="h-10 px-4 rounded-xl text-sm border flex items-center gap-2" title="Compartir en PDF"><Share2 size={15} /> PDF</button>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Eventos ({evByDay(dayModal).length})</div>
                <div className="space-y-2">
                  {evByDay(dayModal).length === 0 && <p className="text-sm text-muted-foreground">Sin eventos.</p>}
                  {evByDay(dayModal).map((e) => { const c = categoryColor(e.category); return (
                    <div key={e.id} className="pp-glass p-3 flex items-center gap-3 group">
                      <div className="w-1 self-stretch rounded-full" style={{ background: c.text }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{e.title}</div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock size={10} /> {new Date(e.start).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}{e.end ? `–${new Date(e.end).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}` : ''} · <span style={{ color: c.text }}>{c.label}</span></div>
                        {e.description && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{e.description}</div>}
                      </div>
                      <button data-testid={`event-share-wa-${e.id}`} title="Compartir por WhatsApp" onClick={() => shareEventWA(e)} className="h-8 w-8 grid place-items-center rounded-lg pp-mint border border-[rgba(62,180,137,0.3)] shrink-0"><Share2 size={13} /></button>
                      <button data-testid={`event-share-pdf-${e.id}`} title="Compartir en PDF" onClick={() => shareEventPDF(e)} className="h-8 w-8 grid place-items-center rounded-lg border shrink-0"><FileDown size={13} /></button>
                      {e.meeting_url && <button onClick={() => play({ id: 'dQw4w9WgXcQ', title: e.title })} className="h-8 px-2.5 rounded-lg text-xs flex items-center gap-1 pp-mint border border-[rgba(62,180,137,0.3)]"><Video size={12} /></button>}
                      <button onClick={() => del(e.id)} className="opacity-0 group-hover:opacity-70 hover:opacity-100 text-red-400"><Trash2 size={14} /></button>
                    </div>
                  ); })}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Tareas para este día ({tasksByDay(dayModal).length})</div>
                <div className="space-y-2">
                  {tasksByDay(dayModal).length === 0 && <p className="text-sm text-muted-foreground">Sin tareas con vencimiento.</p>}
                  {tasksByDay(dayModal).map((t) => { const c = priorityColor(t.priority); return (
                    <div key={t.id} className="pp-glass p-3 flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.text }} />
                      <span className="flex-1 text-sm truncate">{t.title}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.text }}>{c.label}</span>
                    </div>
                  ); })}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">PlannerSpace (reuniones) — {spacesByDay(dayModal).length}</div>
                <div className="space-y-2">
                  {spacesByDay(dayModal).length === 0 && <p className="text-sm text-muted-foreground">Día libre para reuniones.</p>}
                  {spacesByDay(dayModal).map((s) => (
                    <div key={s.id} className="pp-glass p-3 flex items-center gap-3" style={{ borderLeft: `3px solid ${s.color}` }}>
                      <span className="flex-1 text-sm truncate">{s.name}</span>
                      <span className="text-[11px] text-muted-foreground">{s.start_time}–{s.end_time}</span>
                      {s.meeting_url && <span className="text-[10px] pp-gold">video</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
